import { motion } from "framer-motion";
import { Check, Cpu } from "lucide-react";
import SEO from "../components/common/SEO";
import PageHero from "../components/common/PageHero";
import SectionHeading from "../components/common/SectionHeading";
import { Reveal, StaggerGroup, StaggerItem } from "../components/common/Reveal";
import PillButton from "../components/common/PillButton";
import CTASection from "../components/common/CTASection";
import { useContent } from "../context/ContentContext";
import { useStaffing } from "../components/forms/StaffingProvider";

const capabilities = [
  { title: "Design & IP", body: "RTL design, analog/mixed-signal, physical design, and IP integration support." },
  { title: "Verification", body: "Functional and formal verification, UVM environments, and coverage closure." },
  { title: "Validation", body: "Post-silicon bring-up, board and system validation, and characterization." },
  { title: "Embedded & Firmware", body: "Bare-metal, RTOS and driver development for silicon and systems." },
  { title: "Test Engineering", body: "ATE program development, DFT, and production test methodology." },
  { title: "Manufacturing & Quality", body: "Process, yield, reliability and quality engineering across the fab and back-end." },
];

const lifecycle = [
  { step: "01", title: "Design", body: "Architecture, RTL and physical design talent for new silicon." },
  { step: "02", title: "Verify", body: "Verification engineers to close coverage and de-risk tape-out." },
  { step: "03", title: "Validate", body: "Post-silicon validation and characterization to bring products to life." },
  { step: "04", title: "Ramp", body: "Test, process and quality engineers to scale to volume production." },
];

const engagements = [
  { title: "Scale a verification team fast", body: "Stand up a pod of verification engineers to hit an aggressive tape-out window without derailing your roadmap." },
  { title: "Bridge a validation crunch", body: "Bring in specialized post-silicon validation talent for a critical bring-up phase, then flex down as it stabilizes." },
  { title: "Build a dedicated design center", body: "Assemble a long-term dedicated team around a product line, aligned to your tools, flows and cadence." },
];

export default function SemiconductorSpotlight() {
  const { open } = useStaffing();
  const { getIndustry } = useContent();
  const industry = getIndustry("semiconductor-electronics");

  return (
    <>
      <SEO
        title="Semiconductor & Electronics Staffing (USA)"
        description="Specialized semiconductor and electronics engineering staffing across design, verification, validation, embedded, test and manufacturing. Semiconductor staffing USA and electronics staffing USA."
        path="/semiconductor"
      />
      <PageHero
        eyebrow="Spotlight — Semiconductor & Electronics"
        title="Specialized Semiconductor & Electronics Talent"
        subtitle="AMVI helps semiconductor and electronics organizations access hard-to-find engineering talent and technology capability across the full product lifecycle — design, verification, validation, embedded, test and manufacturing."
        image={industry.image}
      >
        <div className="flex flex-col gap-4 sm:flex-row">
          <PillButton onClick={open} size="lg" testId="semi-hero-cta">Build Your Semiconductor Team</PillButton>
          <PillButton to="/industries/semiconductor-electronics" variant="outline" size="lg">Industry Overview</PillButton>
        </div>
      </PageHero>

      {/* Capability areas */}
      <section className="px-6 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="Capabilities"
            title="Depth across the semiconductor lifecycle"
            subtitle="From first RTL to volume production, we provide engineers who understand your domain and your timelines."
            testId="semi-capabilities"
          />
          <StaggerGroup className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map((c) => (
              <StaggerItem key={c.title}>
                <div className="group h-full rounded-2xl border border-white/10 bg-ink-800/40 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-sky-400/40">
                  <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-sky-400">
                    <Cpu className="h-5 w-5" />
                  </div>
                  <h3 className="font-heading text-lg font-bold tracking-tight text-white">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Lifecycle */}
      <section className="border-t border-white/5 px-6 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow="Lifecycle" title="Talent for every stage" testId="semi-lifecycle" />
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {lifecycle.map((l, i) => (
              <motion.div
                key={l.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl border border-white/10 bg-ink-800/50 p-7"
              >
                <div className="font-heading text-4xl font-bold text-sky-400/30">{l.step}</div>
                <h3 className="mt-2 font-heading text-lg font-bold tracking-tight text-white">{l.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{l.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="border-t border-white/5 px-6 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow="Roles We Place" title="Semiconductor & electronics roles" testId="semi-roles" />
          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {industry.roles.map((r) => (
              <div key={r} className="flex items-center gap-3 rounded-xl border border-white/10 bg-ink-800/40 px-4 py-3 text-sm text-slate-300">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-400/15 text-sky-400">
                  <Check className="h-3 w-3" />
                </span>
                {r}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement scenarios */}
      <section className="border-t border-white/5 px-6 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="How We Help"
            title="Engagement scenarios"
            subtitle="Illustrative ways AMVI supports semiconductor teams. Every engagement is shaped around your specific goals."
            testId="semi-engagements"
          />
          <StaggerGroup className="mt-14 grid gap-4 lg:grid-cols-3">
            {engagements.map((e) => (
              <StaggerItem key={e.title}>
                <div className="h-full rounded-2xl border border-white/10 bg-gradient-to-br from-ink-800/80 to-ink-800/30 p-8">
                  <h3 className="font-heading text-lg font-bold tracking-tight text-white">{e.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{e.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <CTASection
        title="Build your semiconductor team"
        subtitle="Tell us the roles and timelines — we'll bring the specialized engineers to deliver."
      />
    </>
  );
}
